import { AppNameMap } from '../../../../../shared/utils/engagementAnalyticsTransformers';

interface SortConfigLike {
    key: string;
    direction: 'asc' | 'desc';
}

export interface OutboundLocationSummary {
    practiceLocation: string;
    outboundCalls: number;
    outboundApptConfirmed: number;
    outboundApptRescheduled: number;
    outboundApptCancelled: number;
    outboundMessagesLeft: number;
    outboundTransferredCalls: number;
    outboundRecalls: number;
    outboundRecallsBooked: number;
    outboundMissedAppointmentCalls: number;
    outboundMissedAppointmentBookings: number;
    totalOutboundActions: number;
}

const countTagOccurrences = (tags: string[], tag: string): number => {
    return tags.filter(t => t === tag).length;
};

export const filterRecordsToLiveApps = <T extends { applicationId: string }>(
    records: T[] | undefined,
    selectedAppLiveStatusMap?: Record<string, boolean>
): T[] => {
    if (!records || records.length === 0) return [];
    if (!selectedAppLiveStatusMap) return records;

    return records.filter(record => selectedAppLiveStatusMap[record.applicationId] !== false);
};

export const aggregateOutboundLocationSummaries = (
    engagements: any[] | undefined,
    appNameMap: AppNameMap
): OutboundLocationSummary[] => {
    if (!engagements || engagements.length === 0) return [];

    const locationMap = new Map<string, Omit<OutboundLocationSummary, 'practiceLocation' | 'totalOutboundActions'> & { outboundAppointmentBookings: number }>();

    const ensureLocation = (location: string) => {
        if (!locationMap.has(location)) {
            locationMap.set(location, {
                outboundCalls: 0,
                outboundApptConfirmed: 0,
                outboundApptRescheduled: 0,
                outboundApptCancelled: 0,
                outboundMessagesLeft: 0,
                outboundTransferredCalls: 0,
                outboundRecalls: 0,
                outboundRecallsBooked: 0,
                outboundMissedAppointmentCalls: 0,
                outboundMissedAppointmentBookings: 0,
                outboundAppointmentBookings: 0
            });
        }

        return locationMap.get(location)!;
    };

    const hasTag = (tags: string[], tag: string): boolean => tags.includes(tag);

    for (const engagement of engagements) {
        const tags = engagement.sessionTags || [];
        const location = appNameMap[engagement.applicationId] || engagement.applicationId || 'Unknown';
        const stats = ensureLocation(location);

        if (hasTag(tags, 'OUTBOUND_CALL')) stats.outboundCalls++;
        if (hasTag(tags, 'OUTBOUND_APPOINTMENT_CONFIRMED')) stats.outboundApptConfirmed += countTagOccurrences(tags, 'OUTBOUND_APPOINTMENT_CONFIRMED');
        if (hasTag(tags, 'OUTBOUND_APPOINTMENT_RESCHEDULED')) stats.outboundApptRescheduled += countTagOccurrences(tags, 'OUTBOUND_APPOINTMENT_RESCHEDULED');
        if (hasTag(tags, 'OUTBOUND_APPOINTMENT_CANCELLED')) stats.outboundApptCancelled += countTagOccurrences(tags, 'OUTBOUND_APPOINTMENT_CANCELLED');
        if (hasTag(tags, 'OUTBOUND_LEFT_MESSAGE')) stats.outboundMessagesLeft += countTagOccurrences(tags, 'OUTBOUND_LEFT_MESSAGE');
        if (hasTag(tags, 'OUTBOUND_CALL_TRANSFERRED')) stats.outboundTransferredCalls += countTagOccurrences(tags, 'OUTBOUND_CALL_TRANSFERRED');
        if (hasTag(tags, 'OUTBOUND_REACTIVATION')) stats.outboundRecalls++;
        if (hasTag(tags, 'OUTBOUND_REACTIVATION') && hasTag(tags, 'OUTBOUND-APPOINTMENT-BOOKED')) stats.outboundRecallsBooked++;
        if (hasTag(tags, 'OUTBOUND_MISSED_APPOINTMENT_CHECK')) stats.outboundMissedAppointmentCalls++;
        if (hasTag(tags, 'OUTBOUND_MISSED_APPOINTMENT_CHECK') && hasTag(tags, 'OUTBOUND-APPOINTMENT-BOOKED')) stats.outboundMissedAppointmentBookings++;
        stats.outboundAppointmentBookings += countTagOccurrences(tags, 'OUTBOUND_APPOINTMENT_BOOKED') + countTagOccurrences(tags, 'OUTBOUND-APPOINTMENT-BOOKED');
    }

    const summaries: OutboundLocationSummary[] = [];

    locationMap.forEach((stats, practiceLocation) => {
        const { outboundAppointmentBookings, ...summaryStats } = stats;
        summaries.push({
            practiceLocation,
            ...summaryStats,
            totalOutboundActions: stats.outboundApptConfirmed + stats.outboundApptRescheduled + stats.outboundApptCancelled + stats.outboundMessagesLeft + outboundAppointmentBookings
        });
    });

    summaries.sort((a, b) => a.practiceLocation.localeCompare(b.practiceLocation));

    return summaries;
};

export const sortOutboundLocationSummaries = (
    data: OutboundLocationSummary[],
    sortConfig: SortConfigLike | null
): OutboundLocationSummary[] => {
    if (!sortConfig || data.length === 0) return data;

    const sorted = data.slice();

    sorted.sort((a, b) => {
        let aVal: string | number;
        let bVal: string | number;

        switch (sortConfig.key) {
            case 'practiceLocation':
                aVal = a.practiceLocation.toLowerCase();
                bVal = b.practiceLocation.toLowerCase();
                break;
            case 'outboundCalls':
                aVal = a.outboundCalls;
                bVal = b.outboundCalls;
                break;
            case 'outboundApptConfirmed':
                aVal = a.outboundApptConfirmed;
                bVal = b.outboundApptConfirmed;
                break;
            case 'outboundApptRescheduled':
                aVal = a.outboundApptRescheduled;
                bVal = b.outboundApptRescheduled;
                break;
            case 'outboundApptCancelled':
                aVal = a.outboundApptCancelled;
                bVal = b.outboundApptCancelled;
                break;
            case 'outboundMessagesLeft':
                aVal = a.outboundMessagesLeft;
                bVal = b.outboundMessagesLeft;
                break;
            case 'outboundTransferredCalls':
                aVal = a.outboundTransferredCalls;
                bVal = b.outboundTransferredCalls;
                break;
            case 'outboundRecalls':
                aVal = a.outboundRecalls;
                bVal = b.outboundRecalls;
                break;
            case 'outboundRecallsBooked':
                aVal = a.outboundRecallsBooked;
                bVal = b.outboundRecallsBooked;
                break;
            case 'outboundMissedAppointmentCalls':
                aVal = a.outboundMissedAppointmentCalls;
                bVal = b.outboundMissedAppointmentCalls;
                break;
            case 'outboundMissedAppointmentBookings':
                aVal = a.outboundMissedAppointmentBookings;
                bVal = b.outboundMissedAppointmentBookings;
                break;
            case 'totalOutboundActions':
                aVal = a.totalOutboundActions;
                bVal = b.totalOutboundActions;
                break;
            default:
                return 0;
        }

        if (typeof aVal === 'string' && typeof bVal === 'string') {
            return sortConfig.direction === 'asc'
                ? aVal.localeCompare(bVal)
                : bVal.localeCompare(aVal);
        }

        return sortConfig.direction === 'asc'
            ? (aVal as number) - (bVal as number)
            : (bVal as number) - (aVal as number);
    });

    return sorted;
};
